import { api, readTotalCount } from '../api/client';
import {
	API_CARD_EXPORT_URL,
	API_COLLECTION_EXPORT_URL,
	API_ITEM_EXPORT_URL,
	API_ITEMS_EXPORT_URL,
	API_TAG_EXPORT_URL,
} from '../utils';
import { slugify } from '../utils/strings';
import { saveFile } from '../platform';

/**
 * Exports (keepr-api). Server-rendered documents for a record or a filtered
 * list — the server does the layout, the client only names and saves the file.
 *
 * Endpoints (all JWT-authenticated through the shared axios client, all
 * fetched as blobs; the server sets Content-Disposition but the client
 * downloads through an object URL, so the filename is built here):
 *   GET /api/items/{id}/export?format=pdf|json
 *       PDF options: attachment_list=1|0 (default 1, lists the item's
 *       attachments), attachments=1|0 (default 0, embeds attachment
 *       contents — photos and PDF pages — into the document).
 *   GET /api/items/export?format=pdf|xlsx|json
 *       &collection_id=&card_id=&include_descendants=&q=&sort_field=&sort_direction=
 *       The exact same filter/sort params the items page sends to
 *       GET /api/items (see hooks/useCollectionItems.js). PDF only:
 *       `layout=table|grid|records` (default table) — table/grid mirror the
 *       page's two views; records renders every item as its own full
 *       item-form PDF concatenated into one document, and honors the
 *       per-item attachment_list/attachments flags. The server caps the
 *       export (EXPORT_ROW_CAP rows for lists, EXPORT_RECORDS_CAP /
 *       EXPORT_RECORDS_EMBED_CAP for records layouts) and says so with
 *       X-Export-Truncated: true + X-Total-Count. xlsx only: `groups=entries`
 *       lays a repeating group out one line per entry (KPR-685); a file cut
 *       short by that shape's line cap says how many items it holds in
 *       X-Export-Item-Count.
 *   GET /api/card-definitions/{id}/export?format=pdf|json
 *   GET /api/collections/{id}/export?format=pdf|json
 *   GET /api/tags/{id}/export?format=pdf|xlsx|json (tags T9)
 *       &card_id=&collection_id= — the tag page's rows, as the page narrows
 *       them; PDF `layout=table|grid|records`, like a list.
 *
 * "Include my private tags" (tags T9, ruled d08): `private_tags=1` on the
 * item, list and tag-page exports — the caller's own private tags in the
 * file, marked as theirs. Off unless the person switched it on; never part of
 * a stored export definition (the stored editor never offers it, and
 * `buildStoredOptions` never writes it).
 *
 * A `target` names what is being exported:
 *   { kind: 'item', itemId }
 *   { kind: 'items', params }        — params as sent to GET /api/items
 *   { kind: 'card', cardId }
 *   { kind: 'collection', collectionId }
 *   { kind: 'tag', tagId, params }   — params: { card_id?, collection_id? }
 */

// The server-side row caps. Mirrored here only to word the modal's hint and
// truncation notes — the X-Export-Truncated header is what actually decides.
export const EXPORT_ROW_CAP = 1000;
// layout=records renders whole item pages, so its caps are far lower —
// and lower again when attachment contents are embedded.
export const EXPORT_RECORDS_CAP = 200;
export const EXPORT_RECORDS_EMBED_CAP = 50;

const targetUrl = (target = {}) => {
	switch (target.kind) {
		case 'item': return API_ITEM_EXPORT_URL(target.itemId);
		case 'items': return API_ITEMS_EXPORT_URL;
		case 'card': return API_CARD_EXPORT_URL(target.cardId);
		case 'collection': return API_COLLECTION_EXPORT_URL(target.collectionId);
		case 'tag': return API_TAG_EXPORT_URL(target.tagId);
		default: throw new Error(`Unknown export target kind: ${target.kind}`);
	}
};

/** `X-Export-Linked-Omitted` — linked rows left out of a merged export (absent = none). */
export const readLinkedOmitted = headers => {
	const n = Number(headers?.['x-export-linked-omitted']);
	return Number.isFinite(n) && n > 0 ? n : 0;
};

/** What a merged export left out, said once, wherever the export lands. */
export const linkedOmittedNote = count => (count > 0
	? `${count === 1 ? '1 record' : `${count.toLocaleString()} records`} from linked collections ${count === 1 ? 'was' : 'were'} left out: ${count === 1 ? 'its collection doesn\'t' : 'their collections don\'t'} let this one keep copies.`
	: null);

const readTruncated = headers =>
	`${headers?.['x-export-truncated'] || ''}`.toLowerCase() === 'true';

/**
 * `X-Export-Item-Count` — how many items an Excel file one line per entry
 * holds when its line cap stopped it short of the row cap (KPR-685). null
 * when the header is absent: the file then holds the usual cap's worth.
 */
export const readItemCount = headers => {
	const n = Number(headers?.['x-export-item-count']);
	return Number.isFinite(n) && n >= 0 && headers?.['x-export-item-count'] !== undefined ? n : null;
};

/**
 * Fetches one export as `{ blob, truncated, totalCount }`.
 *
 * PDF options ride in `options` and are only ever sent when the caller states
 * them — the server's defaults (attachment_list=1, attachments=0,
 * layout=table) stay the server's to own:
 *   attachmentList  boolean → attachment_list=1|0
 *   attachments     boolean → attachments=1|0 (embed attachment contents)
 *   layout          'table'|'grid'|'records'|'matrix' → layout= (list only)
 *   matrixRows/Field/Bucket/Group/Cell/Blank → matrix_* (the pivot: rows
 *                   down, period buckets across, one mark per cell)
 * Excel only:
 *   groups          'entries' → groups=entries: a repeating group one line
 *                   per entry (KPR-685); anything else is not sent, and the
 *                   server's default is one line per item
 * and, for any format:
 *   privateTags     true → private_tags=1 ("Include my private tags", T9);
 *                   sent only when true — the server's default is off
 *   onProgress      (fraction) → called as bytes arrive, 0 to 1, only when the
 *                   response says how long it is (no Content-Length, no call)
 */
export async function fetchExport(target, format, options = {}) {
	const params = { format };
	if (target?.kind === 'items' || target?.kind === 'tag') Object.assign(params, target.params || {});
	if (options.privateTags === true) params.private_tags = 1;
	if (format === 'xlsx' && options.groups === 'entries') params.groups = 'entries';
	if (format === 'pdf') {
		if (options.layout !== undefined) {
			params.layout = options.layout;
		}
		if (options.attachmentList !== undefined) {
			params.attachment_list = options.attachmentList ? 1 : 0;
		}
		if (options.attachments !== undefined) {
			params.attachments = options.attachments ? 1 : 0;
		}
		// The pivot's shape. Sent only for the matrix layout, and only the
		// keys the caller stated — the server owns every default.
		if (options.layout === 'matrix') {
			const matrix = {
				matrix_rows: options.matrixRows,
				matrix_field: options.matrixField,
				matrix_bucket: options.matrixBucket,
				matrix_group: options.matrixGroup,
				matrix_cell: options.matrixCell,
				matrix_from: options.matrixFrom,
				matrix_to: options.matrixTo,
			};
			Object.entries(matrix).forEach(([ key, value ]) => {
				if (value !== undefined && value !== null && value !== '') params[ key ] = value;
			});
			if (options.matrixBlank) params.matrix_blank = 1;
		}
	}
	const { data, headers } = await api.get(targetUrl(target), {
		params,
		responseType: 'blob',
		...(typeof options.onProgress === 'function' ? {
			onDownloadProgress: event => {
				if (event?.total > 0) options.onProgress(Math.min(1, event.loaded / event.total));
			},
		} : {}),
	});
	return {
		blob: data,
		truncated: readTruncated(headers),
		// null, not a length: an export has no page to fall back to.
		totalCount: readTotalCount(headers, null),
		// A merged list's export (`includeLinked=1`) leaves out the linked
		// rows whose link does not allow copies, and counts them here.
		linkedOmitted: readLinkedOmitted(headers),
		// The items the file holds when an entry-shaped Excel file's line cap
		// stopped it short (null otherwise).
		itemCount: readItemCount(headers),
	};
}

/**
 * `<baseName-slug>.<format>`, falling back to the target kind when the base
 * name slugs away to nothing (an untitled item, an all-emoji card name…).
 */
export function buildExportFilename(target, format, baseName) {
	const fallback = {
		item: 'item',
		items: 'items',
		card: 'card',
		collection: 'collection',
		tag: 'tag',
	}[target?.kind] || 'export';
	const slug = slugify(baseName) || fallback;
	return `${slug}.${format}`;
}

/**
 * Saves bytes that came through the authenticated client (a plain <a href>
 * can never fetch them). The platform does the saving: the browser's own save
 * flow on the web, the share sheet in the app (KPR-1668). Returns the promise
 * of that save: await it, so a refusal lands in the caller's catch.
 */
export function downloadBlob(blob, filename, { anchor } = {}) {
	return saveFile(blob, filename, { anchor });
}
